import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { DataGrid } from '../../components/common/datagrid/ResponsiveDataGrid';
import {
    Box,
    TextField,
    Button,
    Stack,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { listWhMaster, unloadWhMaster } from '../../modules/inventoryModule/whMaster';
import { removeWarehouse } from '../../lib/api/inventory';
import WarehouseFormModal from '../../components/inventorymng/WarehouseFormModal';

const WarehouseListContainer = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [pageSize, setPageSize] = useState(20);
    const [openForm, setOpenForm] = useState(false);
    const [editData, setEditData] = useState(null);
    const [removeTarget, setRemoveTarget] = useState(null);
    const [removing, setRemoving] = useState(false);

    const { warehouses, loading } = useSelector(
        ({ inventoryModule, loading }) => ({
            warehouses: inventoryModule.whMaster.warehouses,
            loading: loading['inventory/LIST_WH_MASTER'],
        }),
    );

    useEffect(() => {
        dispatch(listWhMaster());
        return () => dispatch(unloadWhMaster());
    }, [dispatch]);

    const formik = useFormik({
        initialValues: { searchWords: '' },
        validationSchema: yup.object({
            searchWords: yup.string().trim().nullable(),
        }),
        onSubmit: (values, { setSubmitting }) => {
            setSubmitting(true);
            const searchData = {};
            if (values.searchWords) {
                searchData.searchWords = values.searchWords;
            }
            dispatch(listWhMaster(searchData));
            setSubmitting(false);
        },
    });

    const columns = useMemo(
        () => [
            { field: 'id', headerName: 'ID', hide: true },
            {
                field: 'whNm',
                headerName: '창고명',
                minWidth: 200,
                flex: 1.5,
            },
            {
                field: 'whAddr',
                headerName: '주소',
                minWidth: 200,
                flex: 1.5,
            },
            {
                field: 'whCate1',
                headerName: '분류1',
                minWidth: 100,
                flex: 0.7,
            },
            {
                field: 'whCate2',
                headerName: '분류2',
                minWidth: 100,
                flex: 0.7,
            },
            {
                field: 'stockCount',
                headerName: '보유 품목',
                minWidth: 100,
                flex: 0.5,
                type: 'number',
                align: 'center',
                headerAlign: 'center',
                valueFormatter: (params) => `${params.value || 0}종`,
            },
            {
                field: 'totalQty',
                headerName: '총 수량',
                minWidth: 100,
                flex: 0.5,
                type: 'number',
                align: 'center',
                headerAlign: 'center',
                valueFormatter: (params) =>
                    Number(params.value || 0).toLocaleString('ko-KR'),
            },
            {
                field: 'isDefault',
                headerName: '기본',
                minWidth: 80,
                flex: 0.4,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) =>
                    params.value === 'Y' ? (
                        <Chip label="기본" color="primary" size="small" />
                    ) : null,
            },
            {
                field: 'shipYn',
                headerName: '출고용',
                minWidth: 90,
                flex: 0.4,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) =>
                    params.value === 'Y' ? (
                        <Chip label="출고용" color="success" size="small" variant="outlined" />
                    ) : null,
            },
            {
                field: 'crtDt',
                headerName: '등록일',
                minWidth: 160,
                flex: 0.8,
            },
            {
                field: 'actions',
                headerName: '작업',
                minWidth: 100,
                flex: 0.5,
                align: 'center',
                headerAlign: 'center',
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => {
                    const hasStock =
                        Number(params.row.stockCount || 0) > 0 ||
                        Number(params.row.totalQty || 0) > 0;
                    return (
                        <Stack direction="row" spacing={0}>
                            <Tooltip title="수정">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditData(params.row);
                                        setOpenForm(true);
                                    }}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip
                                title={
                                    hasStock
                                        ? '재고가 남아 있어 삭제할 수 없습니다'
                                        : '삭제'
                                }
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={hasStock}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setRemoveTarget(params.row);
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    );
                },
            },
        ],
        [],
    );

    const handleRowClick = (param) => {
        navigate(`/inventorymng/${param.row.whUuid}`);
    };

    const handleAddOpen = () => {
        setEditData(null);
        setOpenForm(true);
    };

    const handleFormClose = (refreshNeeded) => {
        setOpenForm(false);
        setEditData(null);
        if (refreshNeeded) {
            dispatch(listWhMaster());
        }
    };

    const handleRemoveClose = () => {
        setRemoveTarget(null);
    };

    const handleRemoveConfirm = async () => {
        if (!removeTarget) {
            return;
        }
        setRemoving(true);
        try {
            await removeWarehouse(removeTarget.whUuid);
            enqueueSnackbar('창고가 삭제되었습니다', { variant: 'success' });
            setRemoveTarget(null);
            dispatch(listWhMaster());
        } catch (err) {
            const message = err?.response?.data?.message || err.message;
            enqueueSnackbar(message, { variant: 'error' });
        }
        setRemoving(false);
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box
                    component="form"
                    noValidate
                    autoComplete="off"
                    onSubmit={formik.handleSubmit}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                    <TextField
                        variant="standard"
                        size="small"
                        id="searchWords"
                        name="searchWords"
                        label="창고명 검색"
                        value={formik.values.searchWords}
                        onChange={formik.handleChange}
                    />
                    <Button type="submit" variant="contained" color="info" size="small">
                        검색
                    </Button>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button variant="contained" color="primary" onClick={handleAddOpen}>
                        창고 추가
                    </Button>
                </Stack>
            </Box>
            <div style={{ display: 'flex', height: 'calc(100vh - 260px)' }}>
                <div style={{ flexGrow: 1 }}>
                    <DataGrid
                        rows={warehouses || []}
                        columns={columns}
                        loading={loading}
                        pageSize={pageSize}
                        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                        rowsPerPageOptions={[10, 20, 50]}
                        pagination
                        onRowClick={handleRowClick}
                        columnVisibilityModel={{ id: false }}
                        sx={{ cursor: 'pointer' }}
                    />
                </div>
            </div>

            {openForm && (
                <WarehouseFormModal
                    open={openForm}
                    handleClose={handleFormClose}
                    data={editData}
                />
            )}

            {/* 창고 삭제 확인 Dialog */}
            <Dialog open={!!removeTarget} onClose={handleRemoveClose}>
                <DialogTitle>창고 삭제</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`'${removeTarget?.whNm}' 창고를 삭제하시겠습니까?`}
                        <br />
                        삭제된 창고는 복구할 수 없습니다.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleRemoveClose} disabled={removing}>
                        취소
                    </Button>
                    <Button
                        onClick={handleRemoveConfirm}
                        color="error"
                        variant="contained"
                        disabled={removing}
                    >
                        삭제
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WarehouseListContainer;
